import { useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
import { createLogger } from '@web-chat-core/toolkit';
import { getParticipantVideoElement, subscribeParticipantVideoElements, useVideoSnapshot } from './useVideoSnapshot';

const logger = createLogger({ prefix: 'VideoLoadingState' });

interface UseVideoLoadingStateOptions {
    isShowVideo: boolean | Track | undefined;
    isCameraEnabled?: boolean;
    track?: Track;
    participantId: string;
}

/**
 * Хук для отслеживания состояния загрузки видео потока.
 * Video-элемент берётся из registry (VideoRenderer), без DOM-поиска.
 */
export const useVideoLoadingState = ({
    isShowVideo,
    isCameraEnabled = true,
    track,
    participantId,
}: UseVideoLoadingStateOptions) => {
    const isShouldShowVideo = Boolean(isShowVideo) && Boolean(track);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [videoElementForSnapshot, setVideoElementForSnapshot] = useState<HTMLVideoElement | null>(null);

    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const handleCanPlayRef = useRef<(() => void) | null>(null);
    const isMountedRef = useRef(true);

    const { snapshot } = useVideoSnapshot({
        participantId,
        track,
        isShowVideo: isShouldShowVideo,
        isCameraEnabled,
        isVideoReady,
        videoElement: videoElementForSnapshot,
    });

    useEffect(() => {
        if (!isShouldShowVideo || !track) {
            setIsVideoReady(false);
            setVideoElementForSnapshot(null);
            videoElementRef.current = null;
            return;
        }

        isMountedRef.current = true;

        const safeSetReady = (ready: boolean) => {
            if (isMountedRef.current) {
                setIsVideoReady(ready);
            }
        };

        const detachListeners = () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            if (videoElementRef.current && handleCanPlayRef.current) {
                videoElementRef.current.removeEventListener('canplay', handleCanPlayRef.current);
                videoElementRef.current.removeEventListener('loadeddata', handleCanPlayRef.current);
                handleCanPlayRef.current = null;
            }
        };

        const bindVideoElement = (videoElement: HTMLVideoElement) => {
            // Тот же элемент уже привязан — только догоняем ready, не сбрасываем состояние
            if (videoElementRef.current === videoElement) {
                if (videoElement.readyState >= 2) {
                    safeSetReady(true);
                }
                return;
            }

            detachListeners();

            videoElementRef.current = videoElement;
            setVideoElementForSnapshot(videoElement);

            handleCanPlayRef.current = () => {
                if (isMountedRef.current) {
                    safeSetReady(true);
                }
            };

            if (videoElement.readyState >= 2) {
                safeSetReady(true);
            } else {
                videoElement.addEventListener('canplay', handleCanPlayRef.current, { once: true });
                // Также слушаем loadeddata — canplay иногда не приходит после re-attach
                videoElement.addEventListener('loadeddata', handleCanPlayRef.current, { once: true });
            }
        };

        const tryBindFromRegistry = () => {
            try {
                const videoElement = getParticipantVideoElement(participantId);
                if (videoElement) {
                    bindVideoElement(videoElement);
                }
            } catch (error) {
                logger.warn(`Error binding video for participantId: ${participantId}`, error);
                safeSetReady(false);
            }
        };

        tryBindFromRegistry();
        const unsubscribe = subscribeParticipantVideoElements(tryBindFromRegistry);

        // Защита от бесконечного ожидания (2 секунды)
        timeoutIdRef.current = setTimeout(() => {
            if (isMountedRef.current && !videoElementRef.current) {
                safeSetReady(false);
            }
            timeoutIdRef.current = null;
        }, 2000);

        return () => {
            isMountedRef.current = false;
            unsubscribe();
            detachListeners();
            videoElementRef.current = null;
            setVideoElementForSnapshot(null);
        };
    }, [isShouldShowVideo, track, participantId]);

    const isVideoLoading = !isVideoReady && isShouldShowVideo;

    return {
        isVideoLoading,
        isVideoReady,
        snapshot,
    };
};
